/**
 * Connectivity state model (FR-OFF-001, docs/requirements/19-offline-poor-connection.md).
 *
 * Three states — `online`, `degraded`, `offline` — derived from request
 * outcomes + `navigator.onLine`, NEVER `navigator.onLine` alone: flaky venue
 * wifi/4G frequently stays "online" per the browser while requests time out,
 * so we also track consecutive recoverable (network-type) request failures.
 *
 * This module is pure/framework-free so the state machine is unit-testable in
 * isolation; `connectivity-store.ts` wires it to `window` events and the
 * query client.
 */

export type ConnectivityStatus = "online" | "degraded" | "offline";

export interface ConnectivityState {
  status: ConnectivityStatus;
  consecutiveFailures: number;
}

export type ConnectivityEvent =
  | { type: "browser-online" }
  | { type: "browser-offline" }
  | { type: "request-success" }
  /** `recoverable` = network-type failure (timeout, no connection, 5xx). 4xx
   * application errors are never a connectivity signal (FR-OFF-005: never
   * retry 4xx). */
  | { type: "request-failure"; recoverable: boolean };

/** Consecutive recoverable failures before we consider the connection "degraded". */
export const DEGRADE_AFTER_FAILURES = 2;

export const INITIAL_CONNECTIVITY_STATE: ConnectivityState = {
  status: "online",
  consecutiveFailures: 0,
};

export function reduceConnectivity(
  state: ConnectivityState,
  event: ConnectivityEvent,
): ConnectivityState {
  switch (event.type) {
    case "browser-offline":
      return { status: "offline", consecutiveFailures: state.consecutiveFailures };

    case "browser-online":
      // Coming back from a hard offline: treat as "degraded" (not
      // immediately "online") until a request actually succeeds — this is
      // the reconnect-resync moment (FR-OFF-007), not a guarantee yet.
      return state.status === "offline" ? { status: "degraded", consecutiveFailures: 0 } : state;

    case "request-success":
      return { status: "online", consecutiveFailures: 0 };

    case "request-failure": {
      if (!event.recoverable) return state;
      if (state.status === "offline") return state; // browser signal already covers this
      const consecutiveFailures = state.consecutiveFailures + 1;
      return {
        status: consecutiveFailures >= DEGRADE_AFTER_FAILURES ? "degraded" : state.status,
        consecutiveFailures,
      };
    }

    default:
      return state;
  }
}

/**
 * Classifies an error as a connectivity/network-type failure (recoverable —
 * counts toward "degraded" and is safe to retry) vs an application-level 4xx
 * (never a connectivity signal, never retried — FR-OFF-005/009).
 *
 * `BackendApiRequestError` (features/api/backend.ts) carries `statusCode`
 * when the server actually responded; transport failures (timeout, offline,
 * DNS) leave it `undefined`.
 */
export function isRecoverableFailure(error: unknown): boolean {
  if (error && typeof error === "object" && "statusCode" in error) {
    const raw = (error as { statusCode?: unknown }).statusCode;
    const status = typeof raw === "string" ? Number(raw) : raw;
    if (typeof status === "number" && Number.isFinite(status) && status >= 400 && status < 500) {
      return false;
    }
  }
  return true;
}
